import _ from 'lodash';
import { Record as record, List, Map } from 'immutable';

import Route from './Route';
import Station from './Station';
import TicketType from './TicketType';
import Railcard from './Railcard';
import Photocard from './Photocard';
import ValidityPeriod from './ValidityPeriod';
import PassengerRailcard from './PassengerRailcard';
import OrderFulfilment from './order/Fulfilment';

import { FareClasses, FavouriteSeasonExpiringSoonThresholdDays } from '../constants/AppConstants';
import { construct, fields, resolve, resolveAll } from '../helpers/LinkMapper';
import { getDefaults, getResolvers } from '../helpers/Records';
import trainTime from '../lib/TrainTime';
import { matchRailcards } from '../helpers/Railcards';
import { serialize, serializers } from '../helpers/SearchUrls';


const schema = {
    id: [fields.number],
    origin: [resolve.as(Station), new Station()],
    destination: [resolve.as(Station), new Station()],
    adults: [fields.number, 1],
    children: [fields.number, 0],
    passengerCount: [fields.number],
    route: [resolve.as(Route), new Route()],
    ticketType: [resolve.as(TicketType), new TicketType()],
    smartTicketType: [fields.string],
    railcards: [resolveAll.as(Railcard), new List()],
    isSeason: [fields.boolean, false],
    isSmartcard: [fields.boolean, false],
    isOpenReturn: [fields.boolean, false],
    fareClass: [fields.string],
    duration: [fields.string],
    outwardPlusBusOrigin: [resolve.as(Station)],
    outwardPlusBusDestination: [resolve.as(Station)],
    returnPlusBusOrigin: [resolve.as(Station)],
    returnPlusBusDestination: [resolve.as(Station)],
    delivery: [resolve.as(OrderFulfilment)],
    photocard: [resolve.as(Photocard)],
    seasonExpiry: [fields.datetime, null],
    lastUsed: [fields.datetime, null],
};

class Favourite extends record(getDefaults(schema)) {

    static fromJSON (json = {}) {
        return construct(Favourite, json, getResolvers(schema));
    }

    toJSON() {
        return serialize({
            id: serializers.identity,
            origin: serializers.stationNlc,
            destination: serializers.stationNlc,
            adults: serializers.identity,
            children: serializers.identity,
            passengerCount: serializers.identity,
            route: serializers.code,
            ticketType: serializers.code,
            smartTicketType: serializers.identity,
            railcards: serializers.railcardCodes,
            isSmartcard: serializers.identity,
            isFlexi: () => this.isFlexi(),
            isSeason: () => this.isSeason(),
            isOpenReturn: serializers.identity,
            fareClass: () => FareClasses[this.isFirstClass() ? 'FIRST_CLASS' : 'STANDARD'],
            duration: (duration) => duration ? [duration] : null,
            outwardPlusBusOrigin: outwardPlusBusOrigin => outwardPlusBusOrigin ? serializers.stationNlc : null,
            outwardPlusBusDestination: outwardPlusBusDestination => outwardPlusBusDestination ? serializers.stationNlc : null,
            returnPlusBusOrigin: returnPlusBusOrigin => returnPlusBusOrigin ? serializers.stationNlc : null,
            returnPlusBusDestination: returnPlusBusDestination => returnPlusBusDestination ? serializers.stationNlc : null,
            deliveryId: deliveryId => deliveryId ? serializers.identity : null,
            delivery: delivery => delivery ? delivery.id : null,
            photocard: photocard => photocard ? photocard.number : null,
            seasonExpiry: seasonExpiry => seasonExpiry ? serializers.date : null,
            lastUsed: lastUsed => lastUsed ? serializers.date : null,
        }, this);
    }

    get deliveryId () {
        const deliveryId = this.getIn(['delivery', 'id']);
        return deliveryId && Number(deliveryId);
    }

    matchPlusBus (orderTrip) {
        // Get plusbus station NLCs from the `Favourite`
        const reorderablePlusBus = {
            outwardOrigin: this.getIn(['outwardPlusBusOrigin', 'nlc']),
            outwardDestination: this.getIn(['outwardPlusBusDestination', 'nlc']),
            returnOrigin: this.getIn(['returnPlusBusOrigin', 'nlc']),
            returnDestination: this.getIn(['returnPlusBusDestination', 'nlc']),
        };

        // Get plusbus station NLCs from `OrderTrip`
        const plusBusFares = orderTrip.getPlusBus();
        const getPlusBusStation = (direction, terminal) => plusBusFares.getIn([direction, terminal, terminal, 'nlc']);
        const orderTripPlusBus = {
            outwardOrigin: getPlusBusStation('outward', 'origin'),
            outwardDestination: getPlusBusStation('outward', 'destination'),
            returnOrigin: getPlusBusStation('return', 'origin'),
            returnDestination: getPlusBusStation('return', 'destination'),
        };

        // Compare stations
        // Use _.pickBy to strip out null/undefined, etc
        return _.isEqual(_.pickBy(reorderablePlusBus), _.pickBy(orderTripPlusBus));
    }

    /**
     * @param {OrderFare|Fare} fare
     * @param {OrderTrip} orderTrip
     */
    matchFare (fare, orderTrip) {
        return (
            this.origin.nlc === fare.origin.nlc &&
            this.destination.nlc === fare.destination.nlc &&
            this.adults === fare.adults() &&
            this.children === fare.children() &&
            this.route.code === fare.route.code &&
            this.ticketType.code === fare.ticketType.code &&
            this.ticketType.isSeason === fare.isSeason() &&
            this.isOpenReturn === fare.isReturn() &&
            this.isFirstClass() === fare.isFirstClass() &&
            (this.duration && this.ticketType.isSeason && !this.ticketType.isFlexi
                ? this.duration === fare.getOutwardValidityPeriod().toISOString()
                : true) &&
            matchRailcards(this, fare) &&
            (orderTrip ? this.matchPlusBus(orderTrip) : true)
        );
    }

    getRailcardCounts (property = 'code') {
        return this.railcards
            .filterNot((railcard) => railcard.isGroupSave())
            .reduce((counts, railcard) => counts.update(railcard[property], 0, quantity => ++quantity), new Map());
    }

    getPassengerRailcards() {
        return this.getRailcardCounts('code').map(
            (quantity, code) => new PassengerRailcard({ code, quantity })).toList();
    }

    /**
     * @param {OrderFare} orderFare
     * @return {Favourite}
     */
    static fromFare (orderFare) {
        return new Favourite({
            origin: orderFare.origin,
            destination: orderFare.destination,
            adults: orderFare.adults(),
            children: orderFare.children(),
            route: orderFare.route,
            ticketType: orderFare.ticketType,
            smartTicketType: orderFare.smartTicketType,
            railcards: orderFare.getAllRailcards(),
            isSmartcard: orderFare.isSmartcard(),
            isOpenReturn: orderFare.isReturn(),
            fareClass: FareClasses[ orderFare.isFirstClass() ? 'FIRST_CLASS' : 'STANDARD' ],
            duration: orderFare.getOutwardValidityPeriod().toISOString(),
            passenger: orderFare.getPassengers().first()
        });
    }

    hasRailcard () {
        return this.railcards.filterNot((railcard) => railcard.isGroupSave()).size > 0;
    }

    hasPlusBus () {
        return (
            this.outwardPlusBusOrigin ||
            this.outwardPlusBusDestination ||
            this.returnPlusBusOrigin ||
            this.returnPlusBusDestination
        );
    }

    isTravelcard () {
        return (this.origin && this.origin.isTravelcard()) || (this.destination && this.destination.isTravelcard());
    }

    isFlexi () {
        return this.ticketType.isFlexi;
    }

    isSeason () {
        return this.ticketType.isSeason;
    }

    isReorderable () {
        return !(this.ticketType.isPlusBus() || this.ticketType.isAdvance || this.ticketType.isDaySave());
    }

    isReturn () {
        return this.isOpenReturn;
    }

    getAllRailcards () {
        return this.railcards;
    }

    isFirstClass () { return this.ticketType.isFirstClass; }

    getDuration () {
        return trainTime.duration(this.duration);
    }

    getOutwardValidityPeriod () {
        const duration = this.getDuration(),
            days = duration.days(),
            months = duration.months() + duration.years() * 12;

        return new ValidityPeriod({ days, months });
    }

    getPassengers () {
        return List.of(this.passenger);
    }

    getPhotocard () {
        return this.photocard;
    }

    hasPhotocard () {
        return !!this.getPhotocard();
    }

    hasDelivery () {
        return this.delivery;
    }

    getMatchingTripFares (trips) {
        return trips.filter(trip =>
            trip.getJourneyFares()
                .filter(fare => this.matchFare(fare, trip))
                .isEmpty() === false
        );
    }

    hasMatchingTripFares (trips) {
        return this.getMatchingTripFares(trips).size > 0;
    }

    getDaysToExpiry () {
        return this.seasonExpiry
            && this.seasonExpiry.date() - trainTime().date();
    }

    isExpiringSoon () {
        if (!this.seasonExpiry) { return false; }

        const expiryDate = this.seasonExpiry.date();
        const todayDate = trainTime().date();
        const dateDiff = expiryDate - todayDate;

        const isUpcoming = expiryDate >= todayDate;
        const withinThreshold = dateDiff >= 0 && dateDiff < FavouriteSeasonExpiringSoonThresholdDays;

        return isUpcoming && withinThreshold;
    }
}

export default Favourite;



// WEBPACK FOOTER //
// ./src/records/Favourite.js